import React from "react";
import {Autocomplete, Box, Checkbox, Chip, TextField, Typography} from "@mui/material";

export type MultiSelectFilterOption = {value: string, label: string};

/**
 * A compact, fixed-height multi-select filter.
 * A single selection shows as a tag, several as "N selected"; focused, the same summary moves into the
 * placeholder so the field never grows while the user is picking.
 */
export const MultiSelectFilter: React.FC<{
    label: string;
    options: MultiSelectFilterOption[];
    value: string[];
    onChange: (value: string[]) => void;
    width?: number;
}> = ({label, options, value, onChange, width = 200}) => {
    const [focused, setFocused] = React.useState(false);
    const selected = value.map(v => options.find(o => o.value === v) ?? {value: v, label: v});
    const placeholder = !focused || !selected.length
        ? undefined
        : selected.length === 1 ? selected[0].label : `${selected.length} selected`;

    return (
        <Autocomplete<MultiSelectFilterOption, true>
            multiple
            disableCloseOnSelect
            openOnFocus
            size="small"
            options={options}
            value={selected}
            onChange={(_, next) => onChange(next.map(o => o.value))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            getOptionLabel={o => o.label}
            renderValue={(items, getItemProps) => {
                if (focused) return null;
                if (items.length > 1) {
                    return <Typography variant="body2" noWrap sx={{pl: 1}}>{items.length} selected</Typography>;
                }
                const {key, ...itemProps} = getItemProps({index: 0});
                return <Chip key={key} {...itemProps} size="small" label={items[0].label} sx={{maxWidth: '80%'}}/>;
            }}
            renderOption={({key, ...props}, option, {selected: isSelected}) => (
                <Box component="li" key={key} {...props} sx={{gap: 1}}>
                    <Checkbox size="small" checked={isSelected} sx={{p: 0.25}}/>
                    <Typography variant="body2">{option.label}</Typography>
                </Box>
            )}
            renderInput={params => <TextField {...params} label={label} placeholder={placeholder}/>}
            sx={{
                width,
                '& .MuiAutocomplete-inputRoot': {flexWrap: 'nowrap', overflow: 'hidden'},
                '& .MuiAutocomplete-tag': {flexShrink: 0},
                '& .MuiAutocomplete-input': {textOverflow: 'ellipsis'},
                '& .MuiAutocomplete-input::placeholder': {opacity: 1, color: 'text.secondary'},
            }}
        />
    );
};
